/**
 * FAQ accordion with live search, category filtering and deep linking.
 *
 * Progressive enhancement: without JavaScript every question stays readable
 * as a native <details> element, and only the search / filter controls are hidden.
 */

const HIGHLIGHT_TAG = 'mark';

/**
 * Lowercase and strip diacritics so "Retour" matches "retour" and "cafe" matches "café".
 * @param {string} value
 * @returns {string}
 */
function normalize(value) {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return value.replace(/[&<>"']/g, (character) => {
    switch (character) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

class FaqAccordion extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number | undefined} */
  #debounce;

  /** @type {string} */
  #category = '';

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.items = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-faq-item]')]);
    this.searchInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-faq-search]'));
    this.clearButton = this.querySelector('[data-faq-clear]');
    this.chips = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-faq-chip]')]);
    this.resultsCount = this.querySelector('[data-faq-count]');
    this.emptyState = this.querySelector('[data-faq-empty]');
    this.emptyQuery = this.querySelector('[data-faq-empty-query]');
    this.toggleAllButton = this.querySelector('[data-faq-toggle-all]');
    this.singleOpen = this.dataset.singleOpen === 'true';

    // Cache the searchable text and the pristine question label for highlighting.
    for (const item of this.items) {
      item.dataset.haystack = normalize(item.dataset.search);
      const label = item.querySelector('[data-faq-question-text]');
      if (label instanceof HTMLElement) label.dataset.original = label.textContent ?? '';
    }

    this.searchInput?.addEventListener('input', this.#onSearchInput, { signal });
    this.searchInput?.addEventListener('keydown', this.#onSearchKeydown, { signal });
    this.clearButton?.addEventListener('click', this.#onClear, { signal });
    this.toggleAllButton?.addEventListener('click', this.#onToggleAll, { signal });

    for (const chip of this.chips) {
      chip.addEventListener('click', this.#onChipClick, { signal });
    }

    this.addEventListener('toggle', this.#onToggle, { capture: true, signal });
    document.addEventListener('keydown', this.#onDocumentKeydown, { signal });
    window.addEventListener('hashchange', this.#openFromHash, { signal });

    // A form around the input keeps mobile keyboards showing a "search" key.
    this.querySelector('[data-faq-form]')?.addEventListener(
      'submit',
      (event) => event.preventDefault(),
      { signal }
    );

    this.#update();
    this.#openFromHash();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#debounce);
  }

  /** @returns {string} */
  get query() {
    return normalize(this.searchInput?.value ?? '');
  }

  #onSearchInput = () => {
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => this.#update(), 120);
  };

  /** @param {KeyboardEvent} event */
  #onSearchKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.searchInput?.value) return;

    event.stopPropagation();
    this.#onClear();
  };

  /** Pressing "/" anywhere jumps to the search field, the way documentation sites do. */
  #onDocumentKeydown = (event) => {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;

    const target = event.target;
    const isTyping =
      target instanceof HTMLElement &&
      (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

    if (isTyping || !this.searchInput) return;

    event.preventDefault();
    this.searchInput.focus();
  };

  #onClear = () => {
    if (!this.searchInput) return;

    this.searchInput.value = '';
    this.searchInput.focus();
    this.#update();
  };

  /** @param {Event} event */
  #onChipClick = (event) => {
    const chip = event.currentTarget;
    if (!(chip instanceof HTMLElement)) return;

    this.#category = chip.dataset.faqChip ?? '';
    this.#update();
  };

  /** Enforces single-open mode without fighting the native details animation. */
  #onToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    if (!this.singleOpen) return;

    for (const item of this.items) {
      const other = item.querySelector('details');
      if (other instanceof HTMLDetailsElement && other !== details) other.open = false;
    }
  };

  #onToggleAll = () => {
    const visible = this.items.filter((item) => !item.hidden);
    const shouldOpen = visible.some((item) => !item.querySelector('details')?.open);

    for (const item of visible) {
      const details = item.querySelector('details');
      if (details instanceof HTMLDetailsElement) details.open = shouldOpen && !(this.singleOpen && item !== visible[0]);
    }

    this.#syncToggleAllLabel();
  };

  /** Opens and scrolls to the question named in the URL, e.g. /pages/faq#returns-policy */
  #openFromHash = () => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;

    const item = this.querySelector(`#${CSS.escape(id)}`);
    if (!(item instanceof HTMLElement) || !this.items.includes(item)) return;

    // Clear any active filter so the target is never hidden.
    this.#category = '';
    if (this.searchInput) this.searchInput.value = '';
    this.#update();

    const details = item.querySelector('details');
    if (details instanceof HTMLDetailsElement) details.open = true;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    item.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    item.querySelector('summary')?.focus({ preventScroll: true });
  };

  #update() {
    const query = this.query;
    const visible = [];

    for (const item of this.items) {
      const matchesCategory = !this.#category || item.dataset.category === this.#category;
      const matchesQuery = !query || (item.dataset.haystack ?? '').includes(query);
      const show = matchesCategory && matchesQuery;

      item.hidden = !show;
      if (show) visible.push(item);

      this.#highlight(item, query);

      // Collapse hidden answers so a filtered list never reopens half-expanded.
      if (!show) {
        const details = item.querySelector('details');
        if (details instanceof HTMLDetailsElement) details.open = false;
      }
    }

    // A short result list is more useful already expanded.
    if (query && visible.length > 0 && visible.length <= 3) {
      for (const item of visible) {
        const details = item.querySelector('details');
        if (details instanceof HTMLDetailsElement) details.open = !this.singleOpen || item === visible[0];
      }
    }

    this.#syncChips(query);
    this.#syncCount(visible.length, query);
    this.#syncToggleAllLabel();

    this.classList.toggle('faq--empty', visible.length === 0);
    if (this.emptyState instanceof HTMLElement) this.emptyState.hidden = visible.length !== 0;
    if (this.emptyQuery) this.emptyQuery.textContent = this.searchInput?.value.trim() ?? '';
    if (this.clearButton instanceof HTMLElement) this.clearButton.hidden = !this.searchInput?.value;
  }

  /**
   * Wraps the matched substring of the question in <mark>, restoring the
   * original text when the search is cleared.
   *
   * @param {HTMLElement} item
   * @param {string} query
   */
  #highlight(item, query) {
    const label = item.querySelector('[data-faq-question-text]');
    if (!(label instanceof HTMLElement)) return;

    const original = label.dataset.original ?? '';

    if (!query) {
      label.textContent = original;
      return;
    }

    const index = normalize(original).indexOf(query);
    if (index === -1) {
      label.textContent = original;
      return;
    }

    const before = escapeHtml(original.slice(0, index));
    const match = escapeHtml(original.slice(index, index + query.length));
    const after = escapeHtml(original.slice(index + query.length));

    label.innerHTML = `${before}<${HIGHLIGHT_TAG}>${match}</${HIGHLIGHT_TAG}>${after}`;
  }

  /**
   * Marks the active chip and disables the ones with no match for the
   * current search, so the filters never lead to an empty list.
   *
   * @param {string} query
   */
  #syncChips(query) {
    for (const chip of this.chips) {
      const category = chip.dataset.faqChip ?? '';
      const isActive = category === this.#category;

      chip.classList.toggle('faq__chip--active', isActive);
      chip.setAttribute('aria-pressed', String(isActive));

      const matches = this.items.filter((item) => {
        const matchesCategory = !category || item.dataset.category === category;
        const matchesQuery = !query || (item.dataset.haystack ?? '').includes(query);
        return matchesCategory && matchesQuery;
      }).length;

      if (chip instanceof HTMLButtonElement) chip.disabled = matches === 0 && !isActive;
      const badge = chip.querySelector('[data-faq-chip-count]');
      if (badge) badge.textContent = String(matches);
    }
  }

  /**
   * @param {number} count
   * @param {string} query
   */
  #syncCount(count, query) {
    if (!this.resultsCount) return;

    const template =
      count === 1 ? this.dataset.countSingular ?? '' : this.dataset.countPlural ?? '';

    this.resultsCount.textContent = template
      .replace('[count]', String(count))
      .replace('[total]', String(this.items.length));

    this.resultsCount.hidden = !query && !this.#category;
  }

  #syncToggleAllLabel() {
    if (!(this.toggleAllButton instanceof HTMLElement)) return;

    const visible = this.items.filter((item) => !item.hidden);
    const allOpen = visible.length > 0 && visible.every((item) => item.querySelector('details')?.open);

    this.toggleAllButton.textContent = allOpen
      ? this.dataset.collapseAllLabel ?? ''
      : this.dataset.expandAllLabel ?? '';
    this.toggleAllButton.setAttribute('aria-expanded', String(allOpen));
    this.toggleAllButton.hidden = visible.length === 0;
  }
}

if (!customElements.get('faq-accordion')) {
  customElements.define('faq-accordion', FaqAccordion);
}
